<template>
  <div>
    <CVButton v-if="!showDownloadOptions" outline @click.prevent="showDownloadOptions = true">{{
      t('downloadCsv')
    }}</CVButton>
    <div v-else class="flex items-center justify-start gap-4">
      <Select v-model="monthOrYear" :options="monthOrYearOptions" :disabled="isDownloading" />
      <Datepicker
        v-if="monthOrYear === MonthOrYear.MONTH"
        v-model="month"
        v-bind="$attrs"
        month-picker
        :clearable="false"
        :disabled="isDownloading"
      />
      <Datepicker
        v-else
        v-model="year"
        v-bind="$attrs"
        year-picker
        :clearable="false"
        :disabled="isDownloading"
      />
      <CVButton :is-loading="isPending" @click.prevent="downloadCsv">
        {{ t('downloadCsv') }}
      </CVButton>
      <CVButton :is-loading="isPdfsPending" @click.prevent="downloadPdfArchive">
        {{ t('downloadPdfArchive') }}
      </CVButton>
      <CVButton outline :disabled="isDownloading" @click.prevent="showDownloadOptions = false">{{
        t('cancel')
      }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Select from '@/components/Select.vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { computed, ref } from 'vue';
import { useExportInvoicesCsv, useExportInvoicesPdfs } from '@/queries/use-invoices';
import { downloadBlob } from '@/utils/download-file';
import { addLeadingZeros } from '@/utils/format-numbers';
import { useLegalEntityFilter } from '@/hooks/use-legal-entity-filter';

const enum MonthOrYear {
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

const { t } = useI18n();
const { mutateAsync: exportInvoicesCsv, isPending } = useExportInvoicesCsv();
const { mutateAsync: exportInvoicesPdfs, isPending: isPdfsPending } = useExportInvoicesPdfs();
const isDownloading = computed(() => isPending.value || isPdfsPending.value);
// Exports follow the legal entity filter, so each company's books can be exported on their own.
const { legalEntityId } = useLegalEntityFilter();

const showDownloadOptions = ref(false);
const month = ref({
  month: DateTime.now().minus({ month: 1 }).month - 1,
  year: DateTime.now().minus({ month: 1 }).year,
});
const year = ref(DateTime.now().minus({ year: 1 }).year);
const monthOrYear = ref(MonthOrYear.MONTH);

const monthOrYearOptions = [
  {
    label: t('month'),
    value: MonthOrYear.MONTH,
  },
  {
    label: t('year'),
    value: MonthOrYear.YEAR,
  },
];

const selectedPeriod = () => {
  const invoiceDateAfter =
    monthOrYear.value === MonthOrYear.MONTH
      ? DateTime.fromObject({
          year: month.value.year,
          month: month.value.month + 1,
        }).startOf('month')
      : DateTime.fromObject({ year: year.value }).startOf('year');
  const invoiceDateBefore =
    monthOrYear.value === MonthOrYear.MONTH
      ? invoiceDateAfter.endOf('month')
      : invoiceDateAfter.endOf('year');

  return { invoiceDateAfter, invoiceDateBefore };
};

const downloadCsv = async () => {
  const { invoiceDateAfter, invoiceDateBefore } = selectedPeriod();

  const res = await exportInvoicesCsv({
    invoiceDateAfter: invoiceDateAfter.toJSDate(),
    invoiceDateBefore: invoiceDateBefore.toJSDate(),
    legalEntityId: legalEntityId.value ?? undefined,
  });
  const fileName = `${t('invoicesExport')}-${
    monthOrYear.value === MonthOrYear.MONTH
      ? month.value.year + '-' + addLeadingZeros(month.value.month + 1, 2)
      : year.value
  }.csv`;
  await downloadBlob(
    res instanceof Blob ? res : new Blob([res], { type: 'text/csv;charset=utf-8' }),
    fileName,
  );
  showDownloadOptions.value = false;
};

const downloadPdfArchive = async () => {
  const { invoiceDateAfter, invoiceDateBefore } = selectedPeriod();
  const res = await exportInvoicesPdfs({
    invoiceDateAfter: invoiceDateAfter.toJSDate(),
    invoiceDateBefore: invoiceDateBefore.toJSDate(),
    legalEntityId: legalEntityId.value ?? undefined,
  });
  const fileName = `${t('invoicesExport')}-pdfs-${
    monthOrYear.value === MonthOrYear.MONTH
      ? month.value.year + '-' + addLeadingZeros(month.value.month + 1, 2)
      : year.value
  }.zip`;
  await downloadBlob(res, fileName);
  showDownloadOptions.value = false;
};
</script>

<i18n lang="json">
{
  "en": {
    "downloadCsv": "Download CSV ...",
    "downloadPdfArchive": "Download PDF archive",
    "month": "Month",
    "year": "Year",
    "invoicesExport": "invoices-export"
  },
  "de": {
    "downloadCsv": "CSV runterladen ...",
    "downloadPdfArchive": "PDF-Archiv runterladen",
    "month": "Monat",
    "year": "Jahr",
    "invoicesExport": "rechnungen-export"
  },
  "es": {
    "downloadCsv": "Descargar CSV…",
    "downloadPdfArchive": "Descargar archivo PDF",
    "download": "Descargar",
    "month": "Mes",
    "year": "Año",
    "invoicesExport": "exportacion-facturas"
  }
}
</i18n>
